import React from "react";
import { ArrowUpRight, CircleDot, LayoutGrid, Users, Milestone, Tag, Building, Component, GitBranch } from "lucide-react";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { TAGS_KEYS } from "@/other/constants/TagsKeys";
import { EntityRecord } from "@/types/entity";

// --- Create on unmatched ---

export type MetaCreateOnUnmatchedParams = {
  value?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  project?: any;
};

export type CreateOnUnmatchedRecordFn = (trimmedValue: string) => Record<string, unknown>;

export type MetaCreateOnUnmatchedHandler = (
  params: MetaCreateOnUnmatchedParams
) => Promise<number | string | null | void>;

export function createMetaOnUnmatched(
  storeKey: string,
  recordFromValue: CreateOnUnmatchedRecordFn
): MetaCreateOnUnmatchedHandler {
  return async ({ value, project }) => {
    const trimmed = value?.trim();
    if (!trimmed || !project) return null;

    const store = project.getCrudStore(storeKey);
    const newRecord = store.createRecord(recordFromValue(trimmed));
    store.add(newRecord);
    await project.sync();
    return newRecord.id;
  };
}

// --- Meta Item Config Types ---

interface BaseMetaItemConfig {
  label: string;
  placeholder?: string;
  icon?: React.ReactNode;
}

export interface ComboboxMetaItemConfig extends BaseMetaItemConfig {
  type?: "combobox";
  storeKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterFn: (record: any) => boolean;
  showBadges?: boolean;
  multiSelect?: boolean;
  fieldConfig: {
    name: string;
    valueField: string;
    displayField: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  disabled?: (record: EntityRecord, context: any) => boolean;
  /** Maps typed label → store record fields; uses combobox `storeKey` (requires grant_one). */
  createOnUnmatchedRecord?: CreateOnUnmatchedRecordFn;
  optionItemActionRenderer?: (params: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    record: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editorHandler: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    entityManager: any;
  }) => React.ReactNode;
}

export interface InputMetaItemConfig extends BaseMetaItemConfig {
  type: "input";
  fieldName: string;
  inputType?: "text" | "number";
}

export interface IconMetaItemConfig extends BaseMetaItemConfig {
  type: "icon";
  storeKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterFn: (record: any) => boolean;
  fieldConfig: {
    name: string;
    valueField: string;
    displayField: string;
  };
}

export interface TagsMetaItemConfig extends BaseMetaItemConfig {
  type: "tags";
  storeKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterFn: (record: any) => boolean;
  fieldConfig: {
    name: string;
    valueField: string;
    displayField: string;
  };
}

export type MetaItemConfig = ComboboxMetaItemConfig | InputMetaItemConfig | IconMetaItemConfig | TagsMetaItemConfig;

type EntityTypeKey = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];

// --- Editor Meta Items Data ---

export const EDITOR_META_ITEMS: { [K in EntityTypeKey]: MetaItemConfig[] } = {
  [ENTITY_TYPES.TASK]: [
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Status",
      label: "Status",
      placeholder: "Select status",
      icon: <CircleDot className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "statusId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatchedRecord: (value) => ({ tagSet: TAGS_KEYS.status, tagValue: value }),
    },
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Category",
      label: "Category",
      placeholder: "Select category",
      icon: <LayoutGrid className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "categoryId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatchedRecord: (value) => ({ tagSet: TAGS_KEYS.category, tagValue: value }),
    },
    {
      storeKey: STORE_KEYS.boardTeamsStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Team",
      placeholder: "Select team",
      icon: <Users className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "teamId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatchedRecord: (value) => ({
        name: value,
        color: `#${Math.floor(Math.random() * 16777215).toString(16)}`,
      }),
    },
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Phase",
      label: "Phase",
      placeholder: "Select phase",
      icon: <Milestone className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "phaseId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatchedRecord: (value) => ({ tagSet: TAGS_KEYS.phase, tagValue: value }),
    },
    {
      storeKey: STORE_KEYS.clientStore,
      filterFn: () => true,
      label: "Client",
      placeholder: "Select client",
      icon: <Building className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "clientId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatchedRecord: (value) => ({ name: value }),
      optionItemActionRenderer: ({ record, entityManager }) => {
        return (
          <button
            type="button"
            className="opacity-0 group-hover/command-item:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-sm p-1 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              entityManager.openEntityManager({ entityType: ENTITY_TYPES.CLIENT, entityId: record.id });
            }}
          >
            <ArrowUpRight className="size-3" />
          </button>
        );
      }
    },
    {
      type: "tags",
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Tag",
      label: "Tags",
      icon: <Tag className="size-4" />,
      fieldConfig: {
        name: "tagsId",
        valueField: "id",
        displayField: "tagValue"
      }
    }
  ],
  [ENTITY_TYPES.RESOURCE]: [
    {
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Status",
      label: "Status",
      placeholder: "Select status",
      icon: <CircleDot className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "statusId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatchedRecord: (value) => ({ tagSet: TAGS_KEYS.status, tagValue: value }),
    },
    {
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Category",
      label: "Category",
      placeholder: "Select category",
      icon: <LayoutGrid className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "categoryId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatchedRecord: (value) => ({ tagSet: TAGS_KEYS.category, tagValue: value }),
    },
    {
      storeKey: STORE_KEYS.boardTeamsStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Team",
      placeholder: "Select team",
      icon: <Users className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "teamId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatchedRecord: (value) => ({
        name: value,
        color: `#${Math.floor(Math.random() * 16777215).toString(16)}`,
      }),
    },
    {
      type: "icon",
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Icon",
      label: "Icon",
      placeholder: "Select icon",
      fieldConfig: {
        name: "iconId",
        valueField: "id",
        displayField: "tagValue"
      }
    },
    {
      storeKey: STORE_KEYS.resourceTypeStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Type",
      placeholder: "Select type",
      icon: <Component className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "typeId",
        valueField: "id",
        displayField: "description"
      },
      createOnUnmatchedRecord: (value) => ({ description: value }),
    },
    {
      type: "input",
      label: "Sub Type",
      placeholder: "Enter sub type",
      icon: <GitBranch className="size-4" />,
      fieldName: "jobTitle",
      inputType: "text"
    },
    {
      type: "tags",
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Tag",
      label: "Tags",
      icon: <Tag className="size-4" />,
      fieldConfig: {
        name: "tagsId",
        valueField: "id",
        displayField: "tagValue"
      }
    }
  ],
  [ENTITY_TYPES.CLIENT]: [
    {
      storeKey: STORE_KEYS.clientTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Status",
      label: "Status",
      placeholder: "Select status",
      icon: <CircleDot className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "statusId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatchedRecord: (value) => ({ tagSet: TAGS_KEYS.status, tagValue: value }),
    },
    {
      storeKey: STORE_KEYS.clientTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Category",
      label: "Category",
      placeholder: "Select category",
      icon: <LayoutGrid className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "categoryId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatchedRecord: (value) => ({ tagSet: TAGS_KEYS.category, tagValue: value }),
    },
    {
      storeKey: STORE_KEYS.boardTeamsStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Team",
      placeholder: "Select team",
      icon: <Users className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "teamId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatchedRecord: (value) => ({
        name: value,
        color: `#${Math.floor(Math.random() * 16777215).toString(16)}`,
      }),
    },
    {
      type: "tags",
      storeKey: STORE_KEYS.clientTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Tag",
      label: "Tags",
      icon: <Tag className="size-4" />,
      fieldConfig: {
        name: "tagsId",
        valueField: "id",
        displayField: "tagValue"
      }
    }
  ]
};
